<template>
  <Transition name="routing-indicator">
    <div v-if="routing" class="routing-indicator" :style="{ bottom: `${bottom}px` }" role="status" aria-live="polite">
      <span class="routing-indicator__spinner" aria-hidden="true"></span>
      <span>{{ $t('plan_trail.routing_indicator.text') }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { bottomNoticeOffsetPx, elevationPanelTop } from '../utils/bottomNoticePlacement';

/**
 * While a segment is still a straight line waiting for GraphHopper (see
 * utils/pendingSnap.ts), says so — the line is drawn faint and dotted
 * meanwhile. Sits just above the elevation profile.
 */

const store = useStore();
const routing = computed(() => !!store.getters?.['planTrail/routingInProgress']);

const bottom = ref(32);
function place() {
  bottom.value = bottomNoticeOffsetPx({ panelTop: elevationPanelTop(), viewportHeight: window.innerHeight });
}
watch(routing, (now) => { if (now) place(); }, { immediate: true });
onMounted(() => window.addEventListener('resize', place));
onBeforeUnmount(() => window.removeEventListener('resize', place));
</script>

<style scoped>
.routing-indicator {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.85rem 0.35rem 0.7rem;
  border-radius: 999px;
  background: var(--gray, #22242A);
  color: #dcdde3;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  font-size: 0.8rem;
  font-weight: 500;
  pointer-events: none;
}

.routing-indicator__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(253, 223, 0, 0.25);
  border-top-color: #fddf00;
  animation: routing-indicator-spin 0.8s linear infinite;
}

@keyframes routing-indicator-spin {
  to { transform: rotate(360deg); }
}

.routing-indicator-enter-active,
.routing-indicator-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }

.routing-indicator-enter-from,
.routing-indicator-leave-to {
  opacity: 0;
  transform: translate(-50%, 6px);
}
</style>
